import { Gift, Menu, Sun, Moon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { FLOWS_ANCHOR_ID } from "@/components/flowly/Landing/LandingFlowsShowcase/constants";
import { NavLogo } from "./NavLogo";
import { NavLinks } from "./NavLinks";
import { NavUserMenu } from "./NavUserMenu";
import { NavAuthButtons } from "./NavAuthButtons";
import { NavCreateButton } from "./NavCreateButton";
import { NavUpgradeButton } from "./NavUpgradeButton";
import { NavMobileMenu } from "./NavMobileMenu";
import { NavPricingBadge } from "./NavPricingBadge";
import { useNavbar } from "./useNavbar";
import { useNavbarScroll } from "./useNavbarScroll";
import { NAVBAR_OPACITY_TRANSITION_MS, ENV_BANNER } from "./constants";
import { IS_PRODUCTION } from "@/config/version";

export function Navbar() {
  const {
    isOpen,
    setIsOpen,
    isActive,
    handleSignOut,
    user,
    authLoading,
    credits,
    maxCredits,
    creditsPercentage,
    userDisplayInfo,
    isAdmin,
    t,
    isRTL,
  } = useNavbar();
  const { theme, toggleTheme } = useTheme();
  const { openAuthModal } = useAuthModal();
  const location = useLocation();
  const isLandingPage = location.pathname === "/";
  const { navOpacity } = useNavbarScroll(isLandingPage);

  const handleTryFree = () => {
    openAuthModal("signup", { redirectAfterSuccess: `/#${FLOWS_ANCHOR_ID}` });
  };

  return (
    <nav
      className={cn(
        "border-b border-border glass z-50",
        isLandingPage && "sticky",
        // Stick below the fixed dev banner so the nav tucks under it
        // instead of sliding behind it; flush to the top in production.
        isLandingPage && (IS_PRODUCTION ? "top-0" : ENV_BANNER.stickyOffset),
      )}
      style={isLandingPage ? {
        opacity: navOpacity,
        transition: `opacity ${NAVBAR_OPACITY_TRANSITION_MS}ms ease-in-out`,
      } : undefined}
    >
      <PageContainer width="wide" className="h-20 flex items-center justify-between">
        {/* Start: Brand + Create */}
        <div className="flex items-center gap-4">
          <NavLogo />
          <div className="hidden md:block">
            <NavCreateButton
              label={t.nav.createNowFree}
              isLoggedIn={!!user}
            />
          </div>
        </div>

        {/* Center: Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2">
          <NavLinks isActive={isActive} t={t} />
        </div>

        {/* End: Actions */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleTheme}
            className="hidden md:flex px-3 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-all"
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>
          {/* Invite a friend — referral entry point (signed-in, desktop) */}
          {!authLoading && user && (
            <Link to="/invite" className="hidden md:block">
              <button className="relative px-3 py-1.5 bg-background rounded-lg text-xs font-medium text-foreground hover:text-primary transition-colors flex items-center gap-1.5 border border-border/50">
                <Gift className="w-3.5 h-3.5 text-primary" />
                {t.nav.inviteFriends}
              </button>
            </Link>
          )}
          {/* Upgrade Plan button — always shows the current credit balance */}
          <NavUpgradeButton
            credits={credits}
            planName={userDisplayInfo?.planName}
            upgradeLabel={t.nav.upgradePlan}
            manageLabel={t.nav.managePlan}
            className="hidden md:block"
          />

          {!authLoading && (user ? (
            <div className="hidden md:block">
              <NavUserMenu
                userDisplayInfo={userDisplayInfo}
                credits={credits}
                maxCredits={maxCredits}
                creditsPercentage={creditsPercentage}
                isAdmin={isAdmin}
                handleSignOut={handleSignOut}
                t={t}
              />
            </div>
          ) : (
            <div className="hidden md:block">
              <NavAuthButtons
                loginLabel={t.nav.login}
                signupLabel={t.nav.signup}
              />
            </div>
          ))}

          {/* Mobile actions — logged-out: Pricing + Try Free; logged-in: Upgrade + hamburger */}
          {!authLoading && (
            user ? (
              <div className="md:hidden flex items-center gap-2">
                <NavUpgradeButton
                  credits={credits}
                  planName={userDisplayInfo?.planName}
                  upgradeLabel={t.nav.upgradePlan}
                  manageLabel={t.nav.managePlan}
                />
                <button
                  className="text-muted-foreground"
                  onClick={() => setIsOpen(true)}
                  aria-label={t.nav.menu}
                >
                  <Menu />
                </button>
              </div>
            ) : (
              <div className="md:hidden flex items-center gap-2">
                <Link
                  to="/pricing"
                  className="relative px-3 py-2 rounded-xl text-sm font-bold text-foreground border border-border hover:border-foreground transition-colors"
                >
                  {t.nav.pricing}
                  <NavPricingBadge size="sm" />
                </Link>
                <button
                  onClick={handleTryFree}
                  className="px-3 py-2 rounded-xl text-sm font-bold text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
                >
                  {t.nav.tryFree}
                </button>
              </div>
            )
          )}
        </div>
      </PageContainer>

      {/* Mobile Menu */}
      <NavMobileMenu
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isActive={isActive}
        user={user}
        authLoading={authLoading}
        isAdmin={isAdmin}
        handleSignOut={handleSignOut}
        userDisplayInfo={userDisplayInfo}
        credits={credits}
        maxCredits={maxCredits}
        creditsPercentage={creditsPercentage}
        isRTL={isRTL}
        t={t}
      />
    </nav>
  );
}
